import type { DefaultMenuItem, MenuItemDef } from '@amzn/gft-ag-grid-enterprise';
import { DatasetSource, Page } from '@services/api/deckManagement/graphql/schema/API';
import { initializeMetadata } from '@utils/deckManagementEntityHelpers';
import { asGalaxyDrivers, readPageDriverSets } from '@utils/kpiDriverHelpers';
import { AurigaFeatureFlags } from 'types/AurigaContext.types';
import { MenuOption } from 'types/Common.types';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { ContextMenuEnums, DecklineContextMenuOptions } from 'types/Page.types';
import { PageModifiableProperties } from 'types/PageContext.types';
import { PageSidePanelDropDown } from 'types/PageSidePanel.types';

/**
 * Menu items used when there are no rows in the AGGrid component.
 * Used when there are no decklines to display (i.e.: new page).
 */
const NO_ROWS_MENU_ITEMS = [{ value: ContextMenuEnums.INSERT_ROW, label: ContextMenuEnums.INSERT_ROW }];

/**
 * Dev admin actions for a right-clicked deckline, shown under the "Admin Dev Tools" submenu to
 * DEV_TEAM_ADMIN users only. To add a row-level dev action: add its `ContextMenuEnums` value, add an
 * entry here, and handle it in Page.tsx's `contextMenuHandler`.
 */
const DEV_ADMIN_DECKLINE_MENU_ITEMS: MenuOption<ContextMenuEnums>[] = [
  { value: ContextMenuEnums.DEBUG_OUTPUT_DECKLINE, label: ContextMenuEnums.DEBUG_OUTPUT_DECKLINE },
  { value: ContextMenuEnums.DEBUG_OUTPUT_CALC_KPI_IDS, label: ContextMenuEnums.DEBUG_OUTPUT_CALC_KPI_IDS },
];

/** Label of the context menu item whose submenu holds the dev admin actions. */
export const DEV_ADMIN_TOOLS_MENU_LABEL = 'Admin Dev Tools';

/** CSS class applied to the "Admin Dev Tools" menu item (styled in SectionGrid.scss). */
export const DEV_ADMIN_TOOLS_MENU_CLASS = 'section-grid-dev-admin-tools';

/**
 * Handles generating the label for the comments context menu option
 * @param comments
 * @returns string
 */
export const contextMenuCommentsLabel = (comments: EnhancedDeckline['comments']) => {
  const commentLabelParts = [ContextMenuEnums.COMMENT as string];
  if (comments && comments?.length > 0) {
    commentLabelParts.push(`(${comments.length})`);
  }
  return commentLabelParts.join(' ');
};

/**
 * Handles generating the context menu options for a deckline
 * @param isFirstRowAdd - Whether the row is the first row in the AGGrid component
 * @param selectedDeckline - The selected deckline (if any, otherwise null or undefined)
 * @param isEditingDisabled - Whether editing is disabled due to deck lock
 * @returns MenuOption<ContextMenuEnums>[]
 */
export const pageDecklineContextMenuOptions = (
  isFirstRowAdd: boolean,
  selectedDeckline?: EnhancedDeckline | null,
  isEditingDisabled?: boolean,
): MenuOption<ContextMenuEnums>[] => {
  if (isFirstRowAdd) {
    return NO_ROWS_MENU_ITEMS.map((item) => ({
      ...item,
      disabled: isEditingDisabled,
    }));
  }

  const decklineTypeOptions: MenuOption<ContextMenuEnums>[] = [];
  const isBaseKPI = selectedDeckline?.deckline_type === PageSidePanelDropDown.BASE_KPI;

  // Additional Options specific to the BASE_KPI type:
  if (isBaseKPI) {
    decklineTypeOptions.push({
      value: ContextMenuEnums.DISPLAY_NAME_OVERRIDE,
      label: ContextMenuEnums.DISPLAY_NAME_OVERRIDE,
      disabled: isEditingDisabled,
    });
  }

  return [
    { value: ContextMenuEnums.INSERT_ROW_ABOVE, label: ContextMenuEnums.INSERT_ROW_ABOVE, disabled: isEditingDisabled },
    {
      value: ContextMenuEnums.INSERT_ROW_BELOW,
      label: ContextMenuEnums.INSERT_ROW_BELOW,
      divider: true,
      disabled: isEditingDisabled,
    },
    { value: ContextMenuEnums.FOOTNOTES, label: ContextMenuEnums.FOOTNOTES },
    { value: ContextMenuEnums.COMMENT, label: contextMenuCommentsLabel(selectedDeckline?.comments) },
    { value: ContextMenuEnums.DECKLINE_HISTORY, label: ContextMenuEnums.DECKLINE_HISTORY },
    ...decklineTypeOptions,
    { value: ContextMenuEnums.DELETE_DECKLINE, label: ContextMenuEnums.DELETE_DECKLINE, disabled: isEditingDisabled },
  ];
};

/**
 * Handles generating the dev admin context menu options (the "Admin Dev Tools" submenu) for a deckline
 * @param isDevTeamAdmin - Whether the user has DEV_TEAM_ADMIN permissions
 * @param targetDeckline - The right-clicked deckline (if any, otherwise null or undefined)
 * @returns MenuOption<ContextMenuEnums>[] - empty when the submenu should not be shown
 */
export const pageDecklineDevAdminContextMenuOptions = (
  isDevTeamAdmin: boolean,
  targetDeckline?: EnhancedDeckline | null,
): MenuOption<ContextMenuEnums>[] => {
  if (!isDevTeamAdmin) return [];

  // Row-level actions need a right-clicked deckline
  return targetDeckline ? DEV_ADMIN_DECKLINE_MENU_ITEMS : [];
};

/**
 * Whether a deckline context menu option should be shown, given the enabled feature flags.
 * @param value - The context menu option value
 * @param featureFlags - The application feature flags
 * @returns boolean
 */
export const isContextMenuOptionEnabled = (value: ContextMenuEnums, featureFlags: AurigaFeatureFlags): boolean => {
  switch (value) {
    case ContextMenuEnums.FOOTNOTES:
      return featureFlags.enableFootnotes;
    case ContextMenuEnums.COMMENT:
      return featureFlags.enableComments;
    case ContextMenuEnums.DECKLINE_HISTORY:
      return featureFlags.enableAuditHistory;
    default:
      // All other options are not feature-flagged
      return true;
  }
};

/**
 * Converts menu options into AG Grid menu items, dropping options hidden by feature flags. A `divider`
 * becomes an AG Grid `'separator'`, except after the last visible item.
 * @param options - The menu options
 * @param featureFlags - The application feature flags
 * @param onAction - Invoked with the option value when an item is chosen
 * @returns AG Grid menu items
 */
const toAgGridMenuItems = (
  options: MenuOption<ContextMenuEnums>[],
  featureFlags: AurigaFeatureFlags,
  onAction: (actionValue: ContextMenuEnums) => void,
): (DefaultMenuItem | MenuItemDef)[] => {
  const enabledOptions = options.filter((option) => isContextMenuOptionEnabled(option.value, featureFlags));

  return enabledOptions.flatMap((option, index): (DefaultMenuItem | MenuItemDef)[] => {
    const item: MenuItemDef = {
      name: option.label,
      disabled: option.disabled,
      action: () => onAction(option.value),
    };
    return option.divider && index < enabledOptions.length - 1 ? [item, 'separator'] : [item];
  });
};

/**
 * Converts deckline context menu options into AG Grid context menu items. Dev admin options, when any
 * are visible, are appended as a single "Admin Dev Tools" item (after a separator) holding them in its
 * submenu.
 * @param menuOptions - The deckline context menu options
 * @param featureFlags - The application feature flags
 * @param onAction - Invoked with the option value when an item (or submenu item) is chosen
 * @returns AG Grid context menu items
 */
export const toAgGridContextMenuItems = (
  menuOptions: DecklineContextMenuOptions,
  featureFlags: AurigaFeatureFlags,
  onAction: (actionValue: ContextMenuEnums) => void,
): (DefaultMenuItem | MenuItemDef)[] => {
  const items = toAgGridMenuItems(menuOptions.options, featureFlags, onAction);
  const devAdminItems = toAgGridMenuItems(menuOptions.devAdminOptions, featureFlags, onAction);
  if (devAdminItems.length === 0) return items;

  const devAdminToolsItem: MenuItemDef = {
    name: DEV_ADMIN_TOOLS_MENU_LABEL,
    cssClasses: [DEV_ADMIN_TOOLS_MENU_CLASS],
    subMenu: devAdminItems,
  };
  return items.length > 0 ? [...items, 'separator', devAdminToolsItem] : [devAdminToolsItem];
};

/**
 * Initializes a Page object with only properties which are editable on the Page View.
 * @param page
 */
export const initializePageModifiableProperties = (page: Page): PageModifiableProperties => {
  const modifiable = {
    // readPageDriverSets, NOT a spread: spreading the list would produce { '0': {...} }.
    page_drivers: readPageDriverSets(page.page_drivers),
    denominator_drivers: asGalaxyDrivers(readPageDriverSets(page.denominator_drivers)[DatasetSource.GALAXY]) ?? null,
    denominator_label: page.denominator_label ?? null,
  };

  return initializeMetadata(modifiable);
};
